import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);

    // The menu is click driven, so it closes on an outside click, on Escape,
    // or when focus leaves the menu entirely.
    document.addEventListener('click', this.#documentClickListener);
    document.addEventListener('keydown', this.#documentKeydownListener);
    this.addEventListener('focusout', this.#focusOutListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.removeEventListener('click', this.#documentClickListener);
    document.removeEventListener('keydown', this.#documentKeydownListener);
    this.removeEventListener('focusout', this.#focusOutListener);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Close the open submenu when a click lands outside of the menu.
   * @param {MouseEvent} event
   */
  #documentClickListener = (event) => {
    if (!this.#state.activeItem) return;

    // composedPath crosses the shadow root of the overflow list, so clicks on the
    // overflow panel itself are correctly recognised as being inside the menu.
    if (event.composedPath().includes(this)) return;

    this.close();
  };

  /**
   * Close the open submenu on Escape, returning focus to its trigger.
   * @param {KeyboardEvent} event
   */
  #documentKeydownListener = (event) => {
    if (event.key !== 'Escape' || !this.#state.activeItem) return;

    const trigger = this.#triggerOf(this.#state.activeItem);

    this.close();
    trigger?.focus();
  };

  /**
   * Close the open submenu once focus leaves the menu altogether.
   */
  #focusOutListener = () => {
    if (!this.#state.activeItem) return;

    requestAnimationFrame(() => {
      if (!this.#state.activeItem) return;
      // `:focus-within` crosses shadow boundaries, unlike `contains`.
      if (this.matches(':focus-within')) return;
      // Focus was dropped rather than moved elsewhere (e.g. a plain click on the page):
      // the outside click handler takes care of that case.
      if (document.activeElement === document.body || document.activeElement === null) return;

      this.close();
    });
  };

  /**
   * Toggle the submenu of the clicked menu item.
   *
   * Bound to top level items that own a submenu, so a click opens or closes that
   * submenu instead of following the link. Items without a submenu navigate as usual.
   *
   * @param {MouseEvent} event
   */
  toggle = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = /** @type {HTMLElement | null} */ (event.target.closest('.menu-list__link'));
    const listItem = /** @type {HTMLElement | null} */ (event.target.closest('.menu-list__list-item'));
    if (!listItem) return;

    // Let people open the parent link in a new tab/window as usual.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const item = findMenuItem(listItem);
    const isMoreTrigger = listItem.slot === 'more';

    // No submenu to reveal: let the browser follow the link, but tidy up first.
    if (!item || (!findSubmenu(item) && !isMoreTrigger)) {
      this.close();
      return;
    }

    event.preventDefault();

    if (item === this.#state.activeItem) {
      this.close();
    } else {
      this.activate(/** @type {any} */ ({ target: listItem }));
    }

    this.#keepFocus(trigger);
  };

  /**
   * Keep DOM focus on the trigger that was just activated.
   *
   * Safari doesn't focus a control on click, and opening a submenu injects
   * content into the page, both of which can leave focus on `<body>` - which
   * sends assistive technology back to the top of the document instead of
   * announcing the expanded state of the item that was activated.
   *
   * @param {HTMLElement | null} trigger
   */
  #keepFocus(trigger) {
    if (!trigger) return;

    const restore = () => {
      const active = document.activeElement;

      if (active === trigger || !trigger.isConnected) return;
      // Focus is on something real elsewhere: it was moved deliberately, leave it alone.
      if (active && active !== document.body) return;

      trigger.focus({ preventScroll: true });
    };

    restore();
    requestAnimationFrame(restore);
    setTimeout(restore, 150);
  }

  /**
   * Keyboard handling for the top level menu items.
   *
   * Enter and Space are already delivered as clicks by the browser, so only the
   * keys that are specific to a disclosure are handled here.
   *
   * @param {KeyboardEvent} event
   */
  handleKeyDown = (event) => {
    if (!(event.target instanceof Element)) return;

    const listItem = /** @type {HTMLElement | null} */ (event.target.closest('.menu-list__list-item'));
    if (!listItem) return;

    const item = findMenuItem(listItem);

    switch (event.key) {
      case ' ':
        // Buttons already toggle on Space, links do not.
        if (event.target instanceof HTMLButtonElement) return;
        event.preventDefault();
        this.toggle(/** @type {any} */ (event));
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (item !== this.#state.activeItem) this.activate(/** @type {any} */ ({ target: listItem }));
        this.#focusFirstSubmenuLink(item);
        break;
      case 'ArrowUp':
        if (item !== this.#state.activeItem) return;
        event.preventDefault();
        this.close();
        break;
      default:
        break;
    }
  };

  /**
   * Move focus to the first link of an item's submenu, once it is visible.
   * @param {HTMLElement | null} item
   */
  #focusFirstSubmenuLink(item) {
    const submenu = findSubmenu(item);
    if (!submenu) return;

    requestAnimationFrame(() => {
      /** @type {HTMLAnchorElement | null} */ (submenu.querySelector('a[href]'))?.focus();
    });
  }

  /**
   * The element that carries `aria-expanded` for an active item. For the overflow
   * menu that is the "More" button, which is not the active item itself.
   * @param {HTMLElement | null} item
   * @returns {HTMLElement | null}
   */
  #triggerOf(item) {
    if (this.dataset.overflowExpanded === 'true') return this.moreTrigger ?? item;

    return item;
  }

  /**
   * The "More" trigger of the overflow menu.
   * @returns {HTMLElement | null}
   */
  get moreTrigger() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[slot="more"] .menu-list__link'));
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Open the submenu of a menu item immediately.
   * @param {{ target: Element }} event - An event, or any object exposing the menu item's list item as `target`.
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';

      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) delete previousSubmenu.dataset.active;
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
    if(submenu) 
    {
      document.body.classList.add("megamenu_open");
    } else {
      document.body.classList.remove("megamenu_open");
    }

    // The "More" button owns its own aria-expanded, the active item is one of the
    // links inside the overflow panel.
    if (this.moreTrigger) this.moreTrigger.ariaExpanded = (!isDefaultSlot).toString();
  };

  /**
   * Close the currently open submenu, if any.
   */
  close() {
    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    document.body.classList.remove("megamenu_open");
    this.dataset.overflowExpanded = 'false';
    if (this.moreTrigger) this.moreTrigger.ariaExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
